import { useEffect, useState } from 'react'

// Renders a Unix-second timestamp as a human relative label
// ("2 hours ago") with the absolute ISO time on hover. Re-renders
// itself once a minute so list pages don't go stale while the
// operator stares at them.
//
// We re-implement a tiny relative-time function here instead of
// pulling in date-fns; the SPA already keeps its bundle lean and the
// formatting is uncomplicated for our use case.

interface RelativeTimeProps {
  timestamp: number  // Unix seconds — 0 means "no value yet".
  emptyLabel?: string
  prefix?: string
  className?: string
}

const SECOND = 1
const MINUTE = 60 * SECOND
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

export function relativeFromNow(timestampSeconds: number, nowSeconds: number) {
  if (!timestampSeconds) return ''
  const deltaSeconds = nowSeconds - timestampSeconds
  const abs = Math.abs(deltaSeconds)
  const future = deltaSeconds < 0
  const choose = (n: number, unit: string) => {
    const word = n === 1 ? unit : `${unit}s`
    return future ? `in ${n} ${word}` : `${n} ${word} ago`
  }
  if (abs < 45 * SECOND) return future ? 'soon' : 'just now'
  if (abs < 60 * MINUTE) return choose(Math.round(abs / MINUTE), 'minute')
  if (abs < 24 * HOUR) return choose(Math.round(abs / HOUR), 'hour')
  if (abs < 30 * DAY) return choose(Math.round(abs / DAY), 'day')
  if (abs < 365 * DAY) return choose(Math.round(abs / (30 * DAY)), 'month')
  return choose(Math.round(abs / (365 * DAY)), 'year')
}

function absoluteIso(timestampSeconds: number) {
  return new Date(timestampSeconds * 1000).toISOString()
}

export function RelativeTime({
  timestamp,
  emptyLabel = '—',
  prefix,
  className,
}: RelativeTimeProps) {
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000))
  useEffect(() => {
    const handle = setInterval(() => {
      setNow(Math.floor(Date.now() / 1000))
    }, 60_000)
    return () => clearInterval(handle)
  }, [])

  if (!timestamp) {
    return <span className={className}>{emptyLabel}</span>
  }
  const label = relativeFromNow(timestamp, now)
  const tooltip = absoluteIso(timestamp)
  return (
    <time
      dateTime={tooltip}
      title={tooltip}
      className={className}
    >
      {prefix ? `${prefix} ${label}` : label}
    </time>
  )
}
